import React, { useCallback, useMemo } from 'react'
import { alpha, makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { cssVar } from '../../../../../../utils/cssVar'
import SydButton from '../../../../../commonDesign/Button'
import { getEmptyBlocksInGrid } from '../../utils'
import { useNormalizeBlockContent } from '../../hooks/useNormalizeBlockContent'
import { usePreviewDisplayContext } from '../PreviewDisplayContext'
import { ReportObjectBlock } from './ReportObject'
import { GridLines } from './gridLines'

const useStyles = makeStyles((theme) => ({
  content: {
    position: 'relative',
    width: '600px',
    height: '800px',
    margin: '0 auto'
  },
  gridDisplayContent: ({ canEdit }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    gridTemplateColumns: 'repeat(12, 1fr)',
    gridTemplateRows: 'repeat(16, 1fr)',
    display: 'grid',
    zIndex: 10,
    gap: canEdit ? '1px' : 0,
    border: canEdit ? 'var(--border-neutral-normal)' : 0,
    borderRadius: 'var(--border-radius-sm)'
  }),
  blockItemContainer: {
    display: 'grid'
  },
  gridDisplayContentItem: ({ canEdit }) => ({
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    border: canEdit ? '2px solid rgba(0, 0, 0, 0.35)' : 'none',
    cursor: canEdit ? 'pointer' : 'inherit',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: canEdit ? 'var(--border-radius-sm)' : 0,
    transitionDuration: '.125s',
    '&.selected': {
      opacity: 0.95,
      backgroundColor: alpha(cssVar('--color-primary-500'), 0.25),
      borderColor: 'var(--border-color-primary-normal)'
    },
    '&.not-selected': {
      opacity: 0.65
    },
    '&.emptyBlock': {
      borderStyle: 'dashed'
    }
  }),
  deleteButton: {
    position: 'absolute',
    zIndex: 10,
    top: '5px',
    left: '5px'
  }
}))

export const InfographicBlock = ({
  infographicBlock,
  containerSize,
  onChangeContent,
  normalizeContent = true
}) => {
  const { selectedReportObjectId, setSelectedReportObjectId, editingLevel } = usePreviewDisplayContext()
  const isEditable = editingLevel === 'block'

  const {
    content: _content,
    infographicBlockId,
    runtimeConfigValues
  } = infographicBlock

  const classes = useStyles({
    canEdit: isEditable
  })

  const normalizedContent = useNormalizeBlockContent(_content, containerSize)
  const content = normalizeContent ? normalizedContent : _content

  const emptyBlocks = useMemo(() => (
    getEmptyBlocksInGrid({ gridSize: containerSize, content: content ?? [] })
  ), [containerSize, content])

  const removeBlockIndex = useCallback((block) => {
    const newContent = content?.filter((reportObject) => reportObject.id !== block.id)
    onChangeContent?.(newContent)
  }, [content, onChangeContent])

  return (
    <div className={classes.content}>
      <GridLines {...containerSize} />
      <div className={classes.gridDisplayContent}>
        {content?.map((reportObject, i) => (
          <div
            key={`${reportObject.id}_${i}`}
            style={{
              gridArea: `${(reportObject.cords?.y ?? 0) + 1} / ${(reportObject.cords?.x ?? 0) + 1} / ${reportObject.size.rows} span / ${reportObject.size.cols} span`
            }}
            className={clsx(
              classes.gridDisplayContentItem,
              isEditable && selectedReportObjectId === reportObject.id && 'selected',
              (isEditable && selectedReportObjectId && selectedReportObjectId !== reportObject.id) && 'not-selected',
              reportObject.isEmptyBlock ? 'emptyBlock' : ''
            )}
            onClick={() => setSelectedReportObjectId(reportObject.id)}
          >
            {!reportObject.isEmptyBlock && isEditable && (
              <SydButton
                size='xs'
                variant='primary'
                priority='warning'
                onClick={(e) => {
                  e.stopPropagation()
                  removeBlockIndex(reportObject)
                }}
                type='button'
                className={classes.deleteButton}
              >
                Delete
              </SydButton>
            )}
            {reportObject && (
              <ReportObjectBlock
                key={`infographicBlockId_${reportObject.component}`}
                infographicBlockId={infographicBlockId}
                reportObject={reportObject}
                containerSize={containerSize}
                runtimeConfigValues={runtimeConfigValues}
              />
            )}
          </div>
        )
        )}
        {isEditable && emptyBlocks?.map((block, i) => (
          <div
            key={i}
            style={{
              gridArea: `${block.cords.y + 1} / ${block.cords.x + 1} / ${block.size.rows} span / ${block.size.cols} span`
            }}
            className={clsx(
              classes.gridDisplayContentItem,
              selectedReportObjectId === block.id && 'selected',
              (selectedReportObjectId && selectedReportObjectId !== block.id) && 'not-selected',
              block.isEmptyBlock ? 'emptyBlock' : ''
            )}
            onClick={() => {
              onChangeContent([
                ...content,
                { size: block.size, cords: block.cords, id: `new_block_${i}`, component: '' }
              ])
              setSelectedReportObjectId(`new_block_${i}`)
            }}
          >
            <ReportObjectBlock reportObject={block} isEditable={isEditable} />
          </div>
        )
        )}
      </div>
    </div>
  )
}

InfographicBlock.propTypes = {
  infographicBlock: PropTypes.shape({
    content: PropTypes.array,
    infographicBlockId: PropTypes.number,
    runtimeConfigValues: PropTypes.object
  }),
  selectedBlockState: PropTypes.array,
  containerSize: PropTypes.shape({
    cols: PropTypes.number,
    rows: PropTypes.number
  }),
  onChangeContent: PropTypes.func,
  normalizeContent: PropTypes.bool
}
